import React from "react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

export const PAGE_SIZE = 10;

/* ===================== formatters ===================== */
export const fmtMoney = (n?: number | string | null) => {
  if (n == null) return "—";
  const v = Number(n);
  if (!Number.isFinite(v)) return String(n);
  return v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const fmtDate = (iso: string) => {
  try {
    // -> "3 Feb, 2026"
    return new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    })
      .format(new Date(iso))
      .replace(/ (\d{4})$/, ", $1"); // "3 Feb 2026" -> "3 Feb, 2026"
  } catch {
    return iso;
  }
};

export const fmtTs = (iso: string) => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export const dateMatchLabel = (txIso: string, paidIso: string) => {
  const tx = new Date(txIso).getTime();
  const paid = new Date(paidIso).getTime();
  const diffDays = Math.round((tx - paid) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Date match";
  if (Math.abs(diffDays) === 1) return "1 day apart";
  return `${Math.abs(diffDays)} days apart`;
};

/* ===================== pagination helpers ===================== */
export const clampPage = (p: number, totalPages: number) => {
  if (totalPages <= 1) return 1;
  return Math.min(Math.max(1, p), totalPages);
};

export const getPageItems = (page: number, totalPages: number) => {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);

  const items: Array<number | "…"> = [];
  items.push(1);

  const left = Math.max(2, page - 1);
  const right = Math.min(totalPages - 1, page + 1);

  if (left > 2) items.push("…");
  for (let p = left; p <= right; p++) items.push(p);
  if (right < totalPages - 1) items.push("…");

  items.push(totalPages);
  return items;
};

export const rangeLabel = (page: number, total: number) => {
  if (!total) return { from: 0, to: 0 };
  const from = (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);
  return { from, to };
};

/* ===================== small UI atoms ===================== */
export function ScoreBadge({ score }: { score: number }) {
  const tone =
    score >= 95
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : score >= 80
      ? "bg-sky-50 text-sky-800 border-sky-200"
      : score >= 60
      ? "bg-amber-50 text-amber-800 border-amber-200"
      : "bg-slate-50 text-slate-700 border-slate-200";

  return (
    <span
      className={cn(
        "inline-flex items-center justify-center h-6 px-2 rounded border text-[11px] font-semibold tabular-nums",
        tone
      )}
      title={`Match score: ${score}`}
    >
      {score}
    </span>
  );
}

export function SmallPill({
  children,
  variant = "muted",
}: {
  children: React.ReactNode;
  variant?: "muted" | "strong";
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center h-5 px-2 rounded-full text-[10px] font-black uppercase tracking-widest border",
        variant === "strong"
          ? "bg-slate-900 text-white border-slate-900"
          : "bg-white text-slate-600 border-slate-200"
      )}
    >
      {children}
    </span>
  );
}

export function LoadingBlock() {
  return (
    <div className="p-4 space-y-2">
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
    </div>
  );
}

export function EmptyBlock({ text }: { text: string }) {
  return <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">{text}</div>;
}

export function ErrorBlock({ text }: { text: string }) {
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm font-semibold text-rose-700">{text}</div>
  );
}

export function MatchKindBadge({
  kind,
}: {
  kind: "ref_exact" | "auto_exact" | "ref_exact_bulk" | "transfer_ref" | "transfer_auto";
}) {
  if (kind === "ref_exact") {
    return <Badge className="text-[11px]">Exact reference id match</Badge>;
  }
  if (kind === "ref_exact_bulk") {
    return <Badge className="text-[11px]">Bulk reference match</Badge>;
  }
  if (kind === "transfer_ref" || kind === "transfer_auto") {
    return (
      <Badge className="text-[11px] bg-indigo-600 hover:bg-indigo-600">
        Internal transfer
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="text-[11px]">
      Auto exact
    </Badge>
  );
}

export function Pager({
  page,
  setPage,
  totalPages,
  showing,
  total,
  keyPrefix,
}: {
  page: number;
  setPage: (p: number) => void;
  totalPages: number;
  showing: { from: number; to: number };
  total: number;
  keyPrefix: string;
}) {
  return (
    <div className="mt-4 border-t border-slate-200 px-4 py-3 bg-slate-50">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] text-slate-600">
          Showing <span className="text-slate-900 font-bold tabular-nums">{showing.from}</span>–
          <span className="text-slate-900 font-bold tabular-nums">{showing.to}</span> of{" "}
          <span className="text-slate-900 font-bold tabular-nums">{total}</span>
        </div>

        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(ev) => {
                  ev.preventDefault();
                  setPage(clampPage(page - 1, totalPages));
                }}
                className={page <= 1 ? "pointer-events-none opacity-50" : ""}
              />
            </PaginationItem>

            {getPageItems(page, totalPages).map((it, i) =>
              it === "…" ? (
                <PaginationItem key={`${keyPrefix}-el-${i}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={`${keyPrefix}-p-${it}`}>
                  <PaginationLink
                    href="#"
                    isActive={it === page}
                    onClick={(ev) => {
                      ev.preventDefault();
                      setPage(it);
                    }}
                  >
                    {it}
                  </PaginationLink>
                </PaginationItem>
              )
            )}

            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(ev) => {
                  ev.preventDefault();
                  setPage(clampPage(page + 1, totalPages));
                }}
                className={page >= totalPages ? "pointer-events-none opacity-50" : ""}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  );
}

export function TableShell({
  children,
  page,
  setPage,
  totalPages,
  showing,
  total,
  keyPrefix,
}: {
  children: React.ReactNode;
  page: number;
  setPage: (p: number) => void;
  totalPages: number;
  showing: { from: number; to: number };
  total: number;
  keyPrefix: string;
}) {
  return (
    <div className="rounded-lg overflow-hidden bg-white">
      <div className="overflow-x-auto">{children}</div>
      <Pager page={page} setPage={setPage} totalPages={totalPages} showing={showing} total={total} keyPrefix={keyPrefix} />
    </div>
  );
}